import { createElement, useCallback, useMemo, useSyncExternalStore } from "react";

import { AnnotationIcon } from "../../icons/glyphs";
import { formatMediaPlaybackTime, type MediaTimelineMarker } from "../MediaTimelineBar";
import type { PlaybackTransport } from "../Video";
import { hasAnnotationTargetContent } from "./annotationTargetContent";
import { findCapturedFrameAtTimelineTime } from "./capturedFrameIdentity";
import { annotationStudioText, capturedFrameAnnotationSummary } from "./messages";
import type {
  AnnotationFrameDraft,
  AnnotationFrameSummary,
  AnnotationSavedFrameIndexSnapshot,
  AnnotationSetDraft,
  AnnotationStudioLocale,
  AnnotationStudioViewMode,
} from "./types";

export type StudioCapturedFrame = AnnotationFrameDraft | AnnotationFrameSummary;

function isMaterializedFrame(frame: StudioCapturedFrame): frame is AnnotationFrameDraft {
  return "targets" in frame;
}

function annotatedTargetIds(frame: StudioCapturedFrame): readonly string[] {
  return isMaterializedFrame(frame)
    ? Object.values(frame.targets)
        .filter(hasAnnotationTargetContent)
        .map((target) => target.targetId)
    : frame.annotatedTargetIds;
}

export function useCapturedFrameCollection({
  draft,
  savedFrameIndex,
  selectedSequenceId,
  selectedStreamId,
  timelineSequenceId = selectedSequenceId,
  timelineStreamId = selectedStreamId,
  viewMode,
  timelineTransport,
  highlightActiveFrameWhilePlaying = false,
  authoring,
  locale,
  conflictFrameIds,
  dirtyFrameIds,
}: {
  draft: AnnotationSetDraft | null;
  savedFrameIndex?: AnnotationSavedFrameIndexSnapshot | null;
  selectedSequenceId: string | null;
  selectedStreamId: string | null;
  /** Timeline-only context can follow an external result without changing the Studio selection. */
  timelineSequenceId?: string | null;
  timelineStreamId?: string | null;
  viewMode: AnnotationStudioViewMode;
  /** 활성 타임라인(Raw video 또는 live tracking stream). 시간 값이 아니라 store를 받아
   *  playhead 프레임이 실제로 바뀔 때만 리렌더한다. */
  timelineTransport: Pick<PlaybackTransport, "subscribe" | "getSnapshot">;
  /** Configured playback keeps the explicitly selected frame highlighted while the playhead moves. */
  highlightActiveFrameWhilePlaying?: boolean;
  authoring: boolean;
  locale: AnnotationStudioLocale;
  conflictFrameIds: ReadonlySet<string>;
  dirtyFrameIds: ReadonlySet<string>;
}) {
  const draftFrames = draft?.frames;
  const orderedFrameIds = draft?.orderedFrameIds;
  const allFrames = useMemo(() => {
    const projected: { frame: StudioCapturedFrame }[] = [];
    const persistedIds = new Set<string>();
    for (const summary of savedFrameIndex?.orderedFrames ?? []) {
      persistedIds.add(summary.frameId);
      projected.push({ frame: draftFrames?.[summary.frameId] ?? summary });
    }
    for (const frameId of orderedFrameIds ?? []) {
      if (persistedIds.has(frameId)) continue;
      const frame = draftFrames?.[frameId];
      if (frame) projected.push({ frame });
    }
    return projected;
  }, [draftFrames, orderedFrameIds, savedFrameIndex]);
  const streamFrames = useMemo(
    () => allFrames.filter(({ frame }) => frame.streamId === selectedStreamId),
    [allFrames, selectedStreamId],
  );
  const capturedFrameCountsByStream = useMemo(() => {
    const counts = new Map<string, number>();
    for (const { frame } of allFrames) {
      counts.set(frame.streamId, (counts.get(frame.streamId) ?? 0) + 1);
    }
    return counts;
  }, [allFrames]);
  const savedSequenceContentCountsByStream = useMemo(() => {
    if (!savedFrameIndex) return null;
    const capturedSequenceIdsByStream = new Map<string, Set<string>>();
    const annotatedSequenceIdsByStream = new Map<string, Set<string>>();
    for (const frame of savedFrameIndex.orderedFrames) {
      const capturedSequenceIds =
        capturedSequenceIdsByStream.get(frame.streamId) ?? new Set<string>();
      capturedSequenceIds.add(frame.sequenceId);
      capturedSequenceIdsByStream.set(frame.streamId, capturedSequenceIds);
      if (frame.annotatedTargetIds.length === 0) continue;
      const annotatedSequenceIds =
        annotatedSequenceIdsByStream.get(frame.streamId) ?? new Set<string>();
      annotatedSequenceIds.add(frame.sequenceId);
      annotatedSequenceIdsByStream.set(frame.streamId, annotatedSequenceIds);
    }
    return {
      captured: new Map(
        [...capturedSequenceIdsByStream].map(([streamId, sequenceIds]) => [
          streamId,
          sequenceIds.size,
        ]),
      ),
      annotated: new Map(
        [...annotatedSequenceIdsByStream].map(([streamId, sequenceIds]) => [
          streamId,
          sequenceIds.size,
        ]),
      ),
    };
  }, [savedFrameIndex]);
  const { unannotatedFrameCountsBySequenceAndStream, annotatedFrameCountsBySequenceAndStream } =
    useMemo(() => {
      const unannotatedCounts = new Map<string, Map<string, number>>();
      const annotatedCounts = new Map<string, Map<string, number>>();

      for (const { frame } of allFrames) {
        const counts = annotatedTargetIds(frame).length > 0 ? annotatedCounts : unannotatedCounts;
        const streamCounts = counts.get(frame.sequenceId) ?? new Map<string, number>();
        streamCounts.set(frame.streamId, (streamCounts.get(frame.streamId) ?? 0) + 1);
        counts.set(frame.sequenceId, streamCounts);
      }

      return {
        unannotatedFrameCountsBySequenceAndStream: unannotatedCounts,
        annotatedFrameCountsBySequenceAndStream: annotatedCounts,
      };
    }, [allFrames]);
  const contextFrames = useMemo(
    () =>
      allFrames.filter(
        ({ frame }) =>
          frame.sequenceId === timelineSequenceId && frame.streamId === timelineStreamId,
      ),
    [allFrames, timelineSequenceId, timelineStreamId],
  );
  // allFrames는 "저장 프레임(canonical) + 미저장 draft append"라 전역 정렬이 아니다 — 저장분보다
  // 이른 시점의 새 캡처가 뒤에 붙는다. 이진 탐색(findCapturedFrameAtTime)의 오름차순 계약을
  // 여기서 복구한다(마커/패널 표시 순서는 contextFrames가 소유하므로 건드리지 않는다).
  const contextFrameDrafts = useMemo(
    () =>
      contextFrames
        .map(({ frame }) => frame)
        .sort((a, b) => a.timestampSeconds - b.timestampSeconds),
    [contextFrames],
  );
  const findFrameAtTimelineTime = useCallback(
    (timestampSeconds: number) =>
      findCapturedFrameAtTimelineTime(contextFrameDrafts, timestampSeconds),
    [contextFrameDrafts],
  );
  // 프레임 객체 identity가 안정적이라 같은 프레임(또는 undefined)이 유지되는 동안
  // useSyncExternalStore가 리렌더를 걸러준다 — 0.1초 timeline readout 경계에서만 깨어난다.
  const getMatchedFrameAtPlayhead = () =>
    findFrameAtTimelineTime(timelineTransport.getSnapshot().currentTimeSeconds);
  const matchedFrameAtPlayhead = useSyncExternalStore(
    timelineTransport.subscribe,
    getMatchedFrameAtPlayhead,
    getMatchedFrameAtPlayhead,
  );
  const getTimelinePlaying = () => timelineTransport.getSnapshot().playing;
  const timelinePlaying = useSyncExternalStore(
    timelineTransport.subscribe,
    getTimelinePlaying,
    getTimelinePlaying,
  );
  // A passing playhead is not a durable frame-selection intent. Keep the capture/action group
  // stable while playing; pausing at the same readout resolves the existing frame immediately.
  const frameAtPlayhead = timelinePlaying ? undefined : matchedFrameAtPlayhead;
  const highlightedFrameId =
    viewMode === "annotate" || (highlightActiveFrameWhilePlaying && timelinePlaying)
      ? (draft?.activeFrameId ?? null)
      : (matchedFrameAtPlayhead?.frameId ?? null);
  const timelineMarkers: readonly MediaTimelineMarker[] = useMemo(
    () =>
      contextFrames.map(({ frame }) => {
        const annotatedTargetCount = annotatedTargetIds(frame).length;
        const conflict = conflictFrameIds.has(frame.frameId);
        return {
          id: frame.frameId,
          timeSeconds: frame.timestampSeconds,
          active: highlightedFrameId === frame.frameId,
          icon: annotatedTargetCount > 0 ? createElement(AnnotationIcon) : undefined,
          badge: conflict
            ? {
                tone: "error" as const,
                label: annotationStudioText("timelineMarkerConflict", locale),
              }
            : dirtyFrameIds.has(frame.frameId)
              ? { tone: "warning" as const, label: annotationStudioText("unsaved", locale) }
              : undefined,
          label: annotationStudioText(authoring ? "editAnnotation" : "viewAnnotation", locale),
          tooltipContent: `${formatMediaPlaybackTime(frame.timestampSeconds)} · ${capturedFrameAnnotationSummary(
            locale,
            annotatedTargetCount,
          )}`,
        };
      }),
    [authoring, contextFrames, conflictFrameIds, dirtyFrameIds, highlightedFrameId, locale],
  );

  return {
    allFrames,
    streamFrames,
    capturedFrameCountsByStream,
    savedSequenceContentCountsByStream,
    unannotatedFrameCountsBySequenceAndStream,
    annotatedFrameCountsBySequenceAndStream,
    frameAtPlayhead,
    findFrameAtTimelineTime,
    timelineMarkers,
  };
}
